import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SearchForm from '../components/SearchForm';
import FlightCard from '../components/FlightCard';

/* ─── Skeleton card ─────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
      <div className="h-1 bg-gray-200 w-full"></div>
      <div className="p-5 flex flex-col lg:flex-row gap-5 items-center">
        <div className="flex items-center gap-3 min-w-[160px]">
          <div className="w-12 h-12 rounded-xl bg-gray-200"></div>
          <div className="space-y-2">
            <div className="h-3 w-28 bg-gray-200 rounded"></div>
            <div className="h-2 w-16 bg-gray-200 rounded"></div>
          </div>
        </div>
        <div className="flex-grow flex items-center gap-4">
          <div className="h-8 w-16 bg-gray-200 rounded"></div>
          <div className="flex-grow h-px bg-gray-200"></div>
          <div className="h-8 w-16 bg-gray-200 rounded"></div>
        </div>
        <div className="space-y-2 text-right">
          <div className="h-8 w-24 bg-gray-200 rounded ml-auto"></div>
          <div className="h-8 w-20 bg-gray-200 rounded ml-auto"></div>
        </div>
      </div>
    </div>
  );
}

/* ─── Filter sidebar ─────────────────────────────────────────── */
function FilterSidebar({ flights, filters, onChange }) {
  const airlines = useMemo(() => [...new Set(flights.map(f => f.airline || f.airlineCode))], [flights]);
  const minP = useMemo(() => Math.min(...flights.map(f => f.price)), [flights]);
  const maxP = useMemo(() => Math.max(...flights.map(f => f.price)), [flights]);

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-6 sticky top-20">
        <h3 className="font-bold text-gray-800 text-base">Filters</h3>

        {/* Stops */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Stops</p>
          <div className="space-y-2">
            {[['all', 'Any stops'], ['nonstop', 'Non-stop only'], ['stop', 'With stops']].map(([val, label]) => (
              <label key={val} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="stops"
                  value={val}
                  checked={filters.stops === val}
                  onChange={() => onChange({ ...filters, stops: val })}
                  className="accent-blue-600"
                />
                <span className="text-sm text-gray-700 group-hover:text-blue-600 transition-colors">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Departure time */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Departure Time</p>
          <div className="space-y-2">
            {[['all', 'Any time'], ['morning', 'Morning (6am–12pm)'], ['afternoon', 'Afternoon (12pm–6pm)'], ['evening', 'Evening (6pm+)']].map(([val, label]) => (
              <label key={val} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="depTime"
                  value={val}
                  checked={filters.depTime === val}
                  onChange={() => onChange({ ...filters, depTime: val })}
                  className="accent-blue-600"
                />
                <span className="text-sm text-gray-700 group-hover:text-blue-600 transition-colors">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price range */}
        {flights.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Max Price</p>
            <input
              type="range"
              min={minP}
              max={maxP}
              value={filters.maxPrice ?? maxP}
              onChange={e => onChange({ ...filters, maxPrice: Number(e.target.value) })}
              className="w-full accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>${minP.toLocaleString('en-US')}</span>
              <span className="font-semibold text-blue-600">${(filters.maxPrice ?? maxP).toLocaleString('en-US')}</span>
              <span>${maxP.toLocaleString('en-US')}</span>
            </div>
          </div>
        )}

        {/* Airlines */}
        {airlines.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Airlines</p>
            <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
              {airlines.map(a => (
                <label key={a} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!(filters.hiddenAirlines || []).includes(a)}
                    onChange={e => {
                      const hidden = filters.hiddenAirlines || [];
                      onChange({
                        ...filters,
                        hiddenAirlines: e.target.checked
                          ? hidden.filter(x => x !== a)
                          : [...hidden, a],
                      });
                    }}
                    className="accent-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-blue-600 transition-colors">{a}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Reset */}
        <button
          onClick={() => onChange({ stops: 'all', depTime: 'all', maxPrice: null, hiddenAirlines: [] })}
          className="w-full text-xs text-gray-400 hover:text-red-500 transition-colors"
        >
          Reset all filters
        </button>
      </div>
    </aside>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */
const SORT_OPTIONS = [
  { value: 'price', label: '💰 Price' },
  { value: 'duration', label: '⚡ Duration' },
  { value: 'departure', label: '🕐 Departure' },
];

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('price');
  const [dataSource, setDataSource] = useState('');
  const [filters, setFilters] = useState({ stops: 'all', depTime: 'all', maxPrice: null, hiddenAirlines: [] });

  const origin = searchParams.get('origin');
  const destination = searchParams.get('destination');
  const date = searchParams.get('date');
  const returnDate = searchParams.get('returnDate') || '';
  const adults    = parseInt(searchParams.get('adults'))    || parseInt(searchParams.get('passengers')) || 1;
  const children  = parseInt(searchParams.get('children'))  || 0;
  const infants   = parseInt(searchParams.get('infants'))   || 0;
  const passengers = adults + children + infants;
  const travelClass = searchParams.get('class') || 'economy';
  const tripType  = searchParams.get('tripType') || 'oneway';

  useEffect(() => {
    if (!origin || !destination || !date) return;
    setLoading(true);
    setError('');
    setFlights([]);
    axios.get('/api/flights/search', {
      params: { origin, destination, date, returnDate, adults, children, infants, passengers, class: travelClass }
    })
      .then(res => {
        setFlights(res.data.flights || []);
        setDataSource(res.data.source || '');
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to fetch flights.'))
      .finally(() => setLoading(false));
  }, [origin, destination, date, passengers, travelClass]);

  const processedFlights = useMemo(() => {
    const maxP = filters.maxPrice ?? Infinity;
    let list = flights.filter(f => {
      if (filters.stops === 'nonstop' && f.stops !== 0) return false;
      if (filters.stops === 'stop' && f.stops === 0) return false;
      if (f.price > maxP) return false;
      if ((filters.hiddenAirlines || []).includes(f.airline || f.airlineCode)) return false;
      if (filters.depTime !== 'all') {
        const h = parseInt((f.departureTime || '0:00').split(':')[0], 10);
        if (filters.depTime === 'morning' && (h < 6 || h >= 12)) return false;
        if (filters.depTime === 'afternoon' && (h < 12 || h >= 18)) return false;
        if (filters.depTime === 'evening' && h < 18) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sortBy === 'price') return a.price - b.price;
      if (sortBy === 'duration') return (a.durationMinutes || 0) - (b.durationMinutes || 0);
      if (sortBy === 'departure') return (a.departureTime || '').localeCompare(b.departureTime || '');
      return 0;
    });

    if (list.length > 0) {
      const cheapestId = [...list].sort((a, b) => a.price - b.price)[0].id;
      const fastestId = [...list].sort((a, b) => (a.durationMinutes || 0) - (b.durationMinutes || 0))[0].id;
      return list.map(f => ({
        ...f,
        _isCheapest: f.id === cheapestId,
        _isFastest: f.id === fastestId,
      }));
    }
    return list;
  }, [flights, filters, sortBy]);

  const hasSearch = origin && destination && date;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search bar */}
      <div className="bg-white border-b border-gray-100 shadow-sm py-4">
        <div className="max-w-6xl mx-auto px-4">
          <SearchForm initialValues={{ origin, destination, date, passengers, class: travelClass }} />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Results header */}
        {hasSearch && !loading && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div>
              <h2 className="text-xl font-extrabold text-gray-900">
                {processedFlights.length > 0
                  ? <>{processedFlights.length} <span className="text-blue-600">flights</span> found</>
                  : 'No flights found'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {tripType === 'roundtrip' ? '⇄' : '→'} {origin} → {destination}
                {returnDate ? ` · ${date} – ${returnDate}` : ` · ${date}`}
                &nbsp;·&nbsp;
                {adults} ADT{children > 0 ? `, ${children} CHD` : ''}{infants > 0 ? `, ${infants} INF` : ''}
                &nbsp;·&nbsp;
                {travelClass.charAt(0).toUpperCase() + travelClass.slice(1)}
                {dataSource && (
                  <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${
                    dataSource === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {dataSource === 'live' ? '🟢 Live prices' : '🟡 Demo data'}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium hidden sm:block">Sort:</span>
              <div className="flex gap-1">
                {SORT_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    onClick={() => setSortBy(o.value)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                      sortBy === o.value
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-5">
          {/* Sidebar */}
          {!loading && flights.length > 0 && (
            <FilterSidebar flights={flights} filters={filters} onChange={setFilters} />
          )}

          {/* Main content */}
          <div className="flex-grow min-w-0">
            {loading && (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            )}

            {!loading && error && (
              <div className="bg-red-50 border border-red-100 text-red-600 rounded-2xl p-8 text-center">
                <div className="text-4xl mb-3">⚠️</div>
                <p className="font-semibold text-lg">{error}</p>
                <p className="text-sm mt-1 text-red-400">Please try a different route or date.</p>
              </div>
            )}

            {!loading && !error && flights.length > 0 && processedFlights.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="text-5xl mb-4">🔍</div>
                <p className="text-lg font-bold text-gray-700">No flights match your filters</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting or resetting the filters.</p>
                <button
                  onClick={() => setFilters({ stops: 'all', depTime: 'all', maxPrice: null, hiddenAirlines: [] })}
                  className="mt-4 bg-blue-600 text-white text-sm font-semibold px-5 py-2 rounded-xl hover:bg-blue-700 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            )}

            {!loading && !hasSearch && (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="text-5xl mb-4">✈️</div>
                <p className="text-lg font-bold text-gray-700">Search for flights above</p>
                <p className="text-sm text-gray-400 mt-1">Select origin, destination and date to see results.</p>
              </div>
            )}

            {!loading && !error && processedFlights.length > 0 && (
              <div className="space-y-4">
                {processedFlights.map(flight => (
                  <FlightCard
                    key={flight.id}
                    flight={flight}
                    passengers={parseInt(passengers)}
                    travelClass={travelClass}
                  />
                ))}
                <p className="text-center text-xs text-gray-400 pt-2">
                  Showing {processedFlights.length} of {flights.length} flights
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
